/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 */

import { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Error Boundary Component
 *
 * Wraps components to catch and handle runtime errors gracefully
 *
 * @example
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 *
 * @example With custom fallback
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  /**
   * Update state when an error is caught
   */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error details
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Store error info in state
    this.setState({ errorInfo });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // TODO: Send error to monitoring service (e.g., Sentry, LogRocket)
    // Example:
    // Sentry.captureException(error, { extra: errorInfo });
  }

  /**
   * Reset error boundary state
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
    });
  };

  /**
   * Navigate to home page
   */
  handleGoHome = (): void => {
    window.location.href = '/';
  };

  /**
   * Reload the page
   */
  handleReload = (): void => {
    window.location.reload();
  };

  /**
   * Send error report via email using default system email client
   */
  handleReportIssue = (): void => {
    try {
      // Prepare error details for email
      const errorMessage = this.state.error?.message || 'Unknown error';
      const errorStack = this.state.error?.stack || 'No stack trace available';
      const componentStack = this.state.errorInfo?.componentStack || 'No component stack available';
      const timestamp = new Date().toISOString();
      const userAgent = navigator.userAgent;
      const url = window.location.href;

      // Create email subject and body
      const subject = encodeURIComponent(`🚨 Error Report - ${new Date().toLocaleDateString()}`);
      const body = encodeURIComponent(`Hello Support Team,

I encountered an error while using the application. Please find the details below:

ERROR DETAILS:
═══════════════════════════════════════════
• Error Message: ${errorMessage}
• Timestamp: ${timestamp}
• Page URL: ${url}
• Browser: ${userAgent}

TECHNICAL INFORMATION:
═══════════════════════════════════════════
Error Stack:
${errorStack}

Component Stack:
${componentStack}

DESCRIPTION:
═══════════════════════════════════════════
Please describe what you were doing when this error occurred:
[Please provide details here]

═══════════════════════════════════════════
This error report was generated automatically by the application's ErrorBoundary component.

Best regards,
[Your name]`);

      // Create mailto link
      const supportEmail = 'support@arabookverse.com'; // Replace with your actual support email
      const mailtoLink = `mailto:${supportEmail}?subject=${subject}&body=${body}`;

      // Open default email client
      window.location.href = mailtoLink;

      console.log('📧 Email client opened with error report');

      // Also copy to clipboard as backup
      this.copyErrorToClipboard(`Error Report - ${timestamp}

Error: ${errorMessage}
URL: ${url}
Browser: ${userAgent}

${errorStack}`);

    } catch (emailError) {
      console.error('❌ Failed to open email client:', emailError);
      // Fallback: copy to clipboard
      this.copyErrorToClipboard(`Error: ${this.state.error?.message || 'Unknown error'} at ${window.location.href}`);
    }
  };

  /**
   * Copy error information to clipboard as fallback
   */
  copyErrorToClipboard = (errorReport: string): void => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(errorReport).then(() => {
        alert('Error details copied to clipboard. Please paste this information when contacting support.');
      }).catch(() => {
        this.fallbackCopyToClipboard(errorReport);
      });
    } else {
      this.fallbackCopyToClipboard(errorReport);
    }
  };

  /**
   * Fallback clipboard copy for older browsers
   */
  fallbackCopyToClipboard = (text: string): void => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      alert('Error details copied to clipboard. Please paste this information when contacting support.');
    } catch (err) {
      console.error('Fallback copy failed:', err);
      alert('Unable to copy error details. Please manually copy the error message from the console.');
    }

    document.body.removeChild(textArea);
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-destructive/10 p-3">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Something went wrong</CardTitle>
                  <CardDescription>
                    The platform is currently experiencing temporary high traffic.
                    <br />
                    Please try again shortly.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Error message */}
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <span className="font-semibold">Error:</span>{' '}
                  {this.state.error?.message || 'An unexpected error occurred'}
                </AlertDescription>
              </Alert>

              {/* Error details (development mode only) */}
              {import.meta.env.DEV && this.state.errorInfo && (
                <div className="rounded-lg bg-muted p-4">
                  <p className="text-sm font-medium mb-2">Error Details (Dev Mode):</p>
                  <pre className="text-xs overflow-auto max-h-48 text-muted-foreground">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}

              {/* Help text */}
              <div className="text-sm text-muted-foreground">
                <p className="mb-2">You can try the following:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Reload the page to try again</li>
                  <li>Go back to the home page</li>
                  <li>Clear your browser cache and cookies</li>
                  <li>Send an error report to our support team using the email button below</li>
                </ul>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-3">
              <div className="flex gap-3 w-full">
                <Button onClick={this.handleReload} variant="default" className="flex-1">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reload Page
                </Button>
                <Button onClick={this.handleGoHome} variant="outline" className="flex-1">
                  <Home className="mr-2 h-4 w-4" />
                  Go to Home
                </Button>
              </div>
              <Button
                onClick={this.handleReportIssue}
                variant="secondary"
                className="w-full bg-primary/10 hover:bg-primary/20 text-primary border-primary/20"
              >
                <Mail className="mr-2 h-4 w-4" />
                Report Issue via Email
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

/**
 * Hook-based error boundary helper
 * Use this in components that need to trigger error boundary
 */
export const useErrorHandler = () => {
  return (error: Error) => {
    throw error;
  };
};

